import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";

import { addDiscountCode, clearDiscountCode } from "redux/actions/checkout";
import InputField from "shared/components/InputField";
import MentionMeReferee from "./MentionMeReferee";

import { promoOptionsData } from "./PromoCodeOptions";
import styles from "./DiscountCodeField.module.scss";
import { getDiscountError } from "shared/utility";

const DiscountCodeField = ({ className, promoCode, onDiscountRemoved }) => {
  const dispatch = useDispatch();
  const {
    cart,
    checkoutError,
    isApplyingDiscountCode,
    isCheckingOut,
    discountCode: reduxCode,
    discountCodeError,
  } = useSelector((s) => ({ ...s.checkout, ...s.auth }));
  const [discountCode, setDiscountCode] = useState(reduxCode || "");
  const [localError, setLocalError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasDiscount = Boolean(cart.discount);
  const isBusy = isLoading || isApplyingDiscountCode;
  const error = localError || discountCodeError;

  useEffect(() => {
    const discountErr = getDiscountError(checkoutError);
    if (discountErr) {
      setLocalError(discountErr);
    }
  }, [checkoutError, dispatch]);

  useEffect(() => {
    if (!discountCode && cart.discount && cart.discount.code) {
      setDiscountCode(cart.discount.code);
    }
  }, [discountCode, cart.discount, setDiscountCode]);

  useEffect(() => {
    if (promoOptionsData.showBlock && promoCode) {
      setDiscountCode(promoCode);
      dispatch(addDiscountCode(promoCode));
    }
  }, [promoCode, dispatch]);

  const onSubmit = useCallback(() => {
    setIsLoading(true);
    setLocalError(null);
    if (!hasDiscount) {
      dispatch(addDiscountCode(discountCode))
        .then(
          () => {},
          (err) => {
            setLocalError(
              err?.response?.data?.error?.message ||
                "We couldn't apply that discount code.",
            );
          },
        )
        .finally(() => setIsLoading(false));
    } else {
      setDiscountCode("");
      onDiscountRemoved();
      dispatch(clearDiscountCode()).finally(() => setIsLoading(false));
    }
  }, [hasDiscount, discountCode, onDiscountRemoved, dispatch]);

  return (
    <>
      <InputField
        className={cx("checkoutDiscountCodeField gkit-my-2", className)}
        label="Discount code"
        name="discountCode"
      >
        <div className="gkit-d-flex gkit-flex-row">
          <div className={cx("gkit-flex-grow", styles.inputContainer)}>
            <input
              className={cx(
                "gkit-input-md gkit-uppercase",
                Boolean(error) && styles.inputError,
                hasDiscount && !error && styles.inputSuccess,
              )}
              disabled={isBusy || hasDiscount || isCheckingOut}
              placeholder="Code"
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
            />
            {hasDiscount && !error && (
              <img
                alt="Successfully Applied Discount"
                src={require("assets/images/iconDiscountTick.svg").default}
              />
            )}
          </div>
          <button
            className={cx(
              "gkit-btn-md gkit-primary gkit-ms-2",
              isBusy && "gkit-loading",
            )}
            disabled={isBusy || isCheckingOut}
            onClick={onSubmit}
          >
            {hasDiscount ? "Remove" : "Apply"}
          </button>
        </div>
        {error && (
          <p className="gkit-p-13 gkit-bold gkit-text-danger gkit-mb-0 gkit-mt-1">
            {error}
          </p>
        )}
      </InputField>

      <MentionMeReferee />
    </>
  );
};

export default DiscountCodeField;
